import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFF00" },
};

const CHANGED_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFF0F0" },
};

const THIN_BORDER = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const formatIndianNumber = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("en-IN");
};

const formatDate = (d) => {
  if (!d) return "-";
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(d);
};

const formatBookingPeriod = (start, end) => {
  if (!start || !end) return "-";
  return `${formatDate(start)} to ${formatDate(end)}`;
};

const durationDays = (start, end) => {
  if (!start || !end) return "-";
  const s = dayjs(start);
  const e = dayjs(end);
  if (!s.isValid() || !e.isValid()) return "-";
  return String(e.diff(s, "day") + 1);
};

const inventoryProfitLoss = (record) => {
  const specialRate = Number(record?.specialRate) || 0;
  const maintenanceRate = Number(record?.maintenanceRate);
  if (!specialRate || maintenanceRate == null) return "0.00%";
  const pl = ((specialRate - maintenanceRate) / specialRate) * 100;
  return `${pl.toFixed(2)}%`;
};

const suffixProfitLoss = (record) => {
  const purchaseRate = Number(record?.purchaseRate) || 0;
  const specialRate = Number(record?.specialRate) || 0;
  if (!purchaseRate) return "-";
  const pl = ((specialRate - purchaseRate) / purchaseRate) * 100;
  return `${Math.abs(pl).toFixed(2)}%`;
};

const getProductionCharges = (salesOrderData) => {
  const invCharges = (salesOrderData?.inventories || []).reduce(
    (acc, inv) => {
      acc.printingCharge += Number(inv.printingCharge) || 0;
      acc.mountingCharge += Number(inv.mountingCharge) || 0;
      acc.innovationCharge += Number(inv.innovationRate) || 0;
      return acc;
    },
    { printingCharge: 0, mountingCharge: 0, innovationCharge: 0, prnCharge: 0 }
  );

  if (salesOrderData?.suffixInventories?.length) {
    invCharges.prnCharge = salesOrderData.suffixInventories
      .filter((item) => item.suffixCode === "Printing")
      .reduce((total, item) => total + (Number(item.specialRate) || 0), 0);
  }

  return {
    ...invCharges,
    total:
      invCharges.printingCharge +
      invCharges.mountingCharge +
      invCharges.innovationCharge +
      invCharges.prnCharge,
  };
};

const getInventoryTotals = (inventories, bdfType) => {
  let purchaseRate = 0;
  let billingAmount = 0;
  let validItems = 0;
  let profitLossSum = 0;

  (inventories || []).forEach((record) => {
    const pr = Number(record.maintenanceRate) || 0;
    const ba = Number(record.specialRate) || 0;
    purchaseRate += pr;
    billingAmount += ba;
    if (pr && ba) {
      profitLossSum += ((ba - pr) / ba) * 100;
      validItems += 1;
    }
  });

  const plAmount =
    bdfType === "F" ? billingAmount : billingAmount - purchaseRate;
  const avgPl =
    billingAmount !== 0
      ? ((billingAmount - purchaseRate) / billingAmount) * 100
      : 0;

  return {
    purchaseRate: bdfType === "F" ? 0 : Math.round(purchaseRate),
    billingAmount: Math.round(billingAmount),
    plAmount: Math.round(plAmount),
    avgProfitLoss: Math.round(avgPl),
  };
};

const applyCellBorder = (cell) => {
  cell.border = THIN_BORDER;
};

const applyRowBorders = (row, endCol) => {
  for (let col = 1; col <= endCol; col += 1) {
    applyCellBorder(row.getCell(col));
  }
};

const applyMergedRowBorders = (ws, row, startCol, endCol) => {
  for (let col = startCol; col <= endCol; col += 1) {
    applyCellBorder(ws.getCell(row, col));
  }
};

const styleHeaderRow = (row) => {
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    applyCellBorder(cell);
  });
  row.height = 22;
};

const styleSectionTitle = (ws, row, startCol, endCol, title) => {
  ws.mergeCells(row, startCol, row, endCol);
  const cell = ws.getCell(row, startCol);
  cell.value = title;
  cell.font = { bold: true, size: 12 };
  cell.fill = HEADER_FILL;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  applyMergedRowBorders(ws, row, startCol, endCol);
};

const writeSummaryPair = (
  ws,
  row,
  labelCol,
  valueCol,
  label,
  value,
  { bold = false, highlight = false } = {}
) => {
  const labelCell = ws.getCell(row, labelCol);
  const valueCell = ws.getCell(row, valueCol);
  labelCell.value = label;
  valueCell.value = value;
  labelCell.font = { bold };
  valueCell.font = { bold };
  valueCell.alignment = { horizontal: "right" };
  if (highlight) {
    labelCell.fill = HEADER_FILL;
    valueCell.fill = HEADER_FILL;
  }
  applyCellBorder(labelCell);
  applyCellBorder(valueCell);
};

const buildSummaryTotalsSection = (
  ws,
  startRow,
  { inventoryTotals, production, subTotal, grandTotal }
) => {
  let row = startRow;
  const formEndCol = 4;

  styleSectionTitle(ws, row, 1, formEndCol, "SUMMARY TOTALS");
  row += 1;

  const leftRows = [];

  if (inventoryTotals) {
    leftRows.push(
      ["Total Purchase Rate", formatIndianNumber(inventoryTotals.purchaseRate)],
      ["Total Billing Amount", formatIndianNumber(inventoryTotals.billingAmount)],
      ["P/L Amount", formatIndianNumber(inventoryTotals.plAmount)],
      ["Average Profit/Loss", `${inventoryTotals.avgProfitLoss}%`]
    );
  }

  const rightRows = [
    ["Printing Charge", formatIndianNumber(production.printingCharge)],
    ["Mounting Charge", formatIndianNumber(production.mountingCharge)],
  ];

  if (production.innovationCharge > 0) {
    rightRows.push([
      "Innovation Charge",
      formatIndianNumber(production.innovationCharge),
    ]);
  }
  if (production.prnCharge > 0) {
    rightRows.push([
      "Printing Suffix Charge",
      formatIndianNumber(production.prnCharge),
    ]);
  }

  const dataStartRow = row;

  leftRows.forEach(([label, value], index) => {
    writeSummaryPair(ws, dataStartRow + index, 1, 2, label, value);
  });

  const rightLabelCol = 3;
  const rightValueCol = 4;

  rightRows.forEach(([label, value], index) => {
    writeSummaryPair(
      ws,
      dataStartRow + index,
      rightLabelCol,
      rightValueCol,
      label,
      value
    );
  });

  const rightTotalsStartRow = dataStartRow + rightRows.length;

  writeSummaryPair(
    ws,
    rightTotalsStartRow,
    rightLabelCol,
    rightValueCol,
    "Subtotal",
    formatIndianNumber(subTotal),
    { bold: true }
  );

  writeSummaryPair(
    ws,
    rightTotalsStartRow + 1,
    rightLabelCol,
    rightValueCol,
    "Grand Total",
    formatIndianNumber(grandTotal),
    { bold: true, highlight: true }
  );

  return Math.max(
    dataStartRow + leftRows.length,
    rightTotalsStartRow + 2
  );
};

const getCellDisplayLength = (value) => {
  if (value == null) return 0;
  if (typeof value === "object") {
    if (value.richText) {
      return value.richText.map((part) => part.text || "").join("").length;
    }
    if (value.hyperlink) {
      return String(value.text || value.hyperlink).length;
    }
    if (value.formula != null) {
      return String(value.result ?? value.formula).length;
    }
    if (value instanceof Date) return 10;
  }
  if (value instanceof Date) return 10;
  return String(value).length;
};

const autoFitWorksheetColumns = (worksheet, { minWidth = 8, maxWidth = 80, padding = 2 } = {}) => {
  let maxCol = 0;
  worksheet.eachRow((row) => {
    row.eachCell({ includeEmpty: false }, (_cell, colNumber) => {
      if (colNumber > maxCol) maxCol = colNumber;
    });
  });

  for (let col = 1; col <= maxCol; col += 1) {
    let maxLength = 0;
    worksheet.eachRow((row) => {
      const cell = row.getCell(col);
      const len = getCellDisplayLength(cell.value);
      if (len > maxLength) maxLength = len;
    });

    if (maxLength > 0) {
      worksheet.getColumn(col).width = Math.min(
        maxWidth,
        Math.max(minWidth, maxLength + padding)
      );
    }
  }
};

const getLogInventory = (log, id) =>
  log?.inventories?.find((inv) => inv._id === id);

const maybeHighlight = (cell, changed) => {
  if (changed) cell.fill = CHANGED_FILL;
};

const LOG_KEY_MAP = {
  "Party Name": "parentCompanyName",
  "Brand Name": "brandName",
  "GST No.": "gstNumber",
  "BDF Remark": "remark",
  "Brand Segment": "brandSegment",
  "Campaign Name": "campaignName",
  "Campaign Duration (Days)": "campaignDuration",
};

const writeFormField = (
  ws,
  row,
  colLabel,
  colValue,
  label,
  value,
  log,
  salesOrderData,
  hyperlink
) => {
  const labelCell = ws.getCell(row, colLabel);
  labelCell.value = label;
  labelCell.font = { bold: true };
  applyCellBorder(labelCell);

  const valueCell = ws.getCell(row, colValue);

  if (hyperlink) {
    valueCell.value = {
      text: value || hyperlink,
      hyperlink,
    };
    valueCell.font = {
      color: { argb: "FF0563C1" },
      underline: true,
    };
  } else {
    valueCell.value = value ?? "";
  }

  applyCellBorder(valueCell);

  const logField = LOG_KEY_MAP[label];
  if (log && logField && log[logField] !== salesOrderData?.[logField]) {
    maybeHighlight(valueCell, true);
  }
};

const buildFormDetailsSection = (ws, salesOrderData, log, startRow) => {
  const leftFields = [
    ["Party Name", (salesOrderData?.parentCompanyName || "").toUpperCase()],
    ["Brand Name", (salesOrderData?.brandName || "").toUpperCase()],
    ["GST No.", salesOrderData?.gstNumber || ""],
    ["BDF Remark", salesOrderData?.remark || ""],
    ["Brand Segment", (salesOrderData?.brandSegment || "").toUpperCase()],
    ["Campaign Name", (salesOrderData?.campaignName || "").toUpperCase()],
    ["Campaign Duration (Days)", salesOrderData?.campaignDuration ?? ""],
  ];

  const rightFields = [
    ["Sales Person Name", salesOrderData?.salesPerson || ""],
    ["BDF Serial No.", salesOrderData?.bdfStamp || ""],
    ["SO Remark", salesOrderData?.soRemark || ""],
    ["Created By", salesOrderData?.createdByName || "N/A"],
  ];

  if (salesOrderData?.stamp) {
    rightFields.push(["Sales Order ID", salesOrderData.stamp]);
  }

  const roFileUrl = salesOrderData?.roFile || null;

  rightFields.push(
    ["FOC", salesOrderData?.bdfType === "F" ? "Yes" : "No"],
    [
      "RO File",
      roFileUrl || "Not Uploaded",
      roFileUrl || null,
    ]
  );

  if (salesOrderData?.isFromSub && salesOrderData?.subBdfDate) {
    rightFields.push([
      "Production Date",
      formatDate(salesOrderData.subBdfDate),
    ]);
  }

  const rowCount = Math.max(leftFields.length, rightFields.length);
  for (let i = 0; i < rowCount; i += 1) {
    const row = startRow + i;
    if (leftFields[i]) {
      writeFormField(
        ws,
        row,
        1,
        2,
        leftFields[i][0],
        leftFields[i][1],
        log,
        salesOrderData
      );
    }
    if (rightFields[i]) {
      const [fieldLabel, fieldValue, fieldLink] = rightFields[i];
      writeFormField(
        ws,
        row,
        3,
        4,
        fieldLabel,
        fieldValue,
        log,
        salesOrderData,
        fieldLink
      );
    }
  }

  return startRow + rowCount + 1;
};

export async function exportSalesOrderDetailsExcel({
  salesOrderData,
  log,
  subTotal,
  grandTotal,
}) {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Sales Order");

  const formEndCol = 4;

  let row = 1;
  ws.mergeCells(row, 1, row, formEndCol);
  const titleCell = ws.getCell(row, 1);
  titleCell.value = "Sales Order Details";
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "center" };
  titleCell.fill = HEADER_FILL;
  applyMergedRowBorders(ws, row, 1, formEndCol);
  row += 1;

  row = buildFormDetailsSection(ws, salesOrderData, log, row);
  row += 1;

  const isSubBdf =
    salesOrderData?.isFromSub && !!salesOrderData?.subBdfDate;
  const showInventories =
    salesOrderData?.inventories?.length > 0 &&
    (!salesOrderData?.isFromSub ||
      (salesOrderData?.isFromSub && salesOrderData?.subBdfDate));

  if (showInventories) {
    const invColCount = isSubBdf ? 6 : 12;
    styleSectionTitle(
      ws,
      row,
      1,
      invColCount,
      isSubBdf ? "Inventories (Sub BDF)" : "Inventories"
    );
    row += 1;

    const CHARGE_TYPE_LABELS = {
      AGENCY_COMMISSION: "Agency Commission",
      TRANSPORTATION: "Transportation",
    };
    const vendorLabel = (record) => {
      if (record?.lineType) {
        return `${CHARGE_TYPE_LABELS[record.lineType] || record.lineType} - ${record?.ownedBy || "-"}`;
      }
      return record?.isTrading ? record?.ownedBy || "-" : record?.CodeNo || "-";
    };

    let invHeaders;
    if (isSubBdf) {
      invHeaders = [
        "Vendor",
        "Suffix",
        "Location",
        "Size",
        "Printing Charge",
        "Mounting Charge",
      ];
    } else {
      invHeaders = [
        "Vendor",
        "Suffix",
        "Location",
        "Size",
        "Printing Charge",
        "Mounting Charge",
        "Purchase Rate",
        "Billing Amount",
        "Special Rate Per Month",
        "Booking Period",
        "Duration (Days)",
        "Profit/Loss",
      ];
    }

    const headerRow = ws.addRow(invHeaders);
    styleHeaderRow(headerRow);
    row = headerRow.number + 1;

    (salesOrderData.inventories || []).forEach((record) => {
      const logInv = getLogInventory(log, record._id);
      const size = `${record?.width ?? ""} x ${record?.height ?? ""}`;

      if (isSubBdf) {
        const dataRow = ws.addRow([
          vendorLabel(record),
          record?.suffix ?? "",
          record?.locationName ?? "",
          size,
          Number(record?.printingCharge) || 0,
          Number(record?.mountingCharge) || 0,
        ]);
        applyRowBorders(dataRow, invColCount);
      } else {
        const dataRow = ws.addRow([
          vendorLabel(record),
          record?.suffix ?? "",
          record?.locationName ?? "",
          size,
          Number(record?.printingCharge) || 0,
          Number(record?.mountingCharge) || 0,
          salesOrderData?.bdfType === "F"
            ? "-"
            : Number(record?.maintenanceRate) || 0,
          Number(record?.specialRate) || 0,
          Number(record?.specialRatePerMonth) || 0,
          formatBookingPeriod(record?.startDate, record?.endDate),
          durationDays(record?.startDate, record?.endDate),
          inventoryProfitLoss(record),
        ]);
        applyRowBorders(dataRow, invColCount);

        const bookingPeriodCell = dataRow.getCell(10);
        bookingPeriodCell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };

        if (logInv) {
          const highlightIfChanged = (col, current, previous) => {
            if (Number(current || 0) !== Number(previous || 0)) {
              maybeHighlight(dataRow.getCell(col), true);
            }
          };
          highlightIfChanged(5, record?.printingCharge, logInv?.printingCharge);
          highlightIfChanged(6, record?.mountingCharge, logInv?.mountingCharge);
          highlightIfChanged(7, record?.maintenanceRate, logInv?.maintenanceRate);
          highlightIfChanged(8, record?.specialRate, logInv?.specialRate);
          highlightIfChanged(
            9,
            record?.specialRatePerMonth,
            logInv?.specialRatePerMonth
          );
          if (
            record?.startDate !== logInv?.startDate ||
            record?.endDate !== logInv?.endDate
          ) {
            maybeHighlight(dataRow.getCell(10), true);
          }
        }
      }
    });

    row = ws.lastRow.number + 1;
    row += 1;
  }

  const suffixInventories = salesOrderData?.suffixInventories || [];
  if (suffixInventories.length > 0 && !salesOrderData?.isFromSub) {
    styleSectionTitle(ws, row, 1, 8, "Suffix Inventories");
    row += 1;

    const suffixHeaders = [
      "Vendor",
      "Suffix Code",
      "Suffix Info",
      "Purchase Rate",
      "Billing Amount",
      "Start Date",
      "End Date",
      "Profit/Loss",
    ];
    const suffixHeaderRow = ws.addRow(suffixHeaders);
    styleHeaderRow(suffixHeaderRow);
    row = suffixHeaderRow.number + 1;

    suffixInventories.forEach((record) => {
      const dataRow = ws.addRow([
        record?.vendor ?? "",
        record?.suffixCode ?? "",
        record?.suffixInfo ?? "",
        Number(record?.purchaseRate) || 0,
        Number(record?.specialRate) || 0,
        formatDate(record?.startDate),
        formatDate(record?.endDate),
        suffixProfitLoss(record),
      ]);
      applyRowBorders(dataRow, suffixHeaders.length);
    });
    row = ws.lastRow.number + 2;
  }

  row += 1;
  const production = getProductionCharges(salesOrderData);
  const inventoryTotals =
    showInventories && !isSubBdf
      ? getInventoryTotals(salesOrderData.inventories, salesOrderData?.bdfType)
      : null;

  row = buildSummaryTotalsSection(ws, row, {
    inventoryTotals,
    production,
    subTotal,
    grandTotal,
  });

  autoFitWorksheetColumns(ws);

  const stamp = salesOrderData?.stamp || "SalesOrder";
  const brand = (salesOrderData?.brandName || "Export")
    .replace(/[\\/*?:[\]]/g, "")
    .slice(0, 40);
  const fileName = `SO_${stamp}_${brand}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName
  );
}
